import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProperty } from '../context/PropertyContext';
import { usePermissions } from '../hooks/usePermissions';
import { supabase } from '../integrations/supabase/client';
import { Property } from '../types';
import { formatPhone, parsePhone } from '../utils/masks';

import { propertyTypeToEng, transactionTypeToEng } from '../utils/translations';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { LeadDetailModal } from '../components/LeadDetailModal';
import PropertyDetailModal from '../components/PropertyDetailModal';
import ForcePasswordChangeModal from '../components/ForcePasswordChangeModal';

import PropertyList from '../components/PropertyList';
import CondominiumList from '../components/CondominiumList';
import ClientList from '../components/ClientList';

import RHDashboard from '../components/RHDashboard';
import ComercialDashboard from '../components/ComercialDashboard';
import TemplatesContrato from '../components/comercial/TemplatesContrato';
import ConfiguracaoCabecalhoContrato from '../components/comercial/ConfiguracaoCabecalhoContrato';
import { UserManagement } from '../components/UserManagement';
import { AutoUpdateManager } from '../components/AutoUpdateManager';
import { FilaDistribuicao } from '../components/FilaDistribuicao';
import { LeadsNaoAtendidos } from '../components/LeadsNaoAtendidos';
import { useAutoSubscribePush } from '../hooks/useAutoSubscribePush';
import { FichaCaptacaoList } from '../components/FichaCaptacaoList';
import { FichaCaptacaoForm } from '../components/FichaCaptacaoForm';
import { FichaCaptacaoView } from '../components/FichaCaptacaoView';
import { cleanAllDuplicateImages } from '../utils/cleanDuplicateImages';
import { TestVideoUpdate } from '../components/TestVideoUpdate';


import PropertyTypeSelector from '../components/PropertyTypeSelector';
import PropertyForm from '../components/PropertyForm';
import { 
  Home, 
  Users, 
  TrendingUp, 
  Building, 
  LogOut,
  BarChart3,
  Settings,
  Menu,
  X,
  UserPlus,
  User,
  Mail,
  Phone,
  Award,
  Edit,
  Save,
  Lock,
  Eye,
  EyeOff,
  Bell,
  Bed,
  Bath,
  
  Car,
  Ruler,
  Clock,
  ChevronDown,
  ChevronRight,
  FileText,
  Briefcase
} from 'lucide-react';

const DashboardPage: React.FC = () => {
  console.log('📱 DashboardPage: Inicializando componente...');
  
  try {
  
  // Ativar push notifications automaticamente
  useAutoSubscribePush();
  
  const { user, profile, loading } = useAuth();
  const { properties, refreshProperties } = useProperty();
  const { canAccessSettings, canAccessUsers } = usePermissions();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  console.log('📱 DashboardPage: Estado atual:', { 
    user: user?.email, 
    profile: profile?.first_name, 
    loading,
    propertiesCount: properties?.length
  });
  
  // Initialize activeTab based on URL parameter
  const initialTab = searchParams.get('tab') || 'dashboard';
  const [activeTab, setActiveTab] = useState<'dashboard' | 'properties' | 'condominiums' | 'clients' | 'vendas' | 'locacoes' | 'rh' | 'usuarios' | 'perfil' | 'settings' | 'fila' | 'fichas-captacao' | 'templates-contrato' | 'cabecalho-contratos'>(
    initialTab as 'dashboard' | 'properties' | 'condominiums' | 'clients' | 'vendas' | 'locacoes' | 'rh' | 'usuarios' | 'perfil' | 'settings' | 'fila' | 'fichas-captacao' | 'templates-contrato' | 'cabecalho-contratos'
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [configSubmenuOpen, setConfigSubmenuOpen] = useState(false);
  const [comercialSubmenuOpen, setComercialSubmenuOpen] = useState(false);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedPropertyType, setSelectedPropertyType] = useState('');
  const [selectedCondominiumId, setSelectedCondominiumId] = useState<string | undefined>();
  const [viewingProperty, setViewingProperty] = useState<Property | undefined>();

  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    phone: '',
    creci: ''
  });
  const [displayProfilePhone, setDisplayProfilePhone] = useState('');
  const [fullProfile, setFullProfile] = useState<any>(null);

  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);

  // Notificações state
  const [hasNotifications, setHasNotifications] = useState(false);
  const [notificacoes, setNotificacoes] = useState<Array<{
    id: string;
    title: string;
    message: string;
    type: 'lead' | 'property' | 'system';
    data: any;
    read: boolean;
    created_at: string;
  }>>([]);
  const [showNotificacoes, setShowNotificacoes] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [showLeadDetail, setShowLeadDetail] = useState(false);
  
  // Fichas de Captação state
  const [showFichaCaptacaoForm, setShowFichaCaptacaoForm] = useState(false);
  const [showFichaCaptacaoView, setShowFichaCaptacaoView] = useState(false);
  const [selectedFicha, setSelectedFicha] = useState<any>(null);
  
  // Abrir lead diretamente via URL: ?lead=UUID
  useEffect(() => {
    const urlLead = searchParams.get('lead');
    if (urlLead) {
      setSelectedLeadId(urlLead);
      setShowLeadDetail(true);
    }
  }, [searchParams]);

  // Carregar notificações
  useEffect(() => {
    if (user) {
      loadNotifications();
      
      // Configurar realtime para novas notificações
      const channel = supabase
        .channel('notifications-changes')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            console.log('Nova notificação recebida:', payload);
            setNotificacoes(prev => [payload.new as any, ...prev]);
            setHasNotifications(true);
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  const loadNotifications = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      
      setNotificacoes((data || []).map(item => ({
        id: item.id,
        title: item.title,
        message: item.message,
        type: item.type as 'lead' | 'property' | 'system',
        data: item.data,
        read: item.read,
        created_at: item.created_at
      })));
      setHasNotifications(data?.some(n => !n.read) || false);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    }
  };

  const markNotificationAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) throw error;

      setNotificacoes(prev => 
        prev.map(n => n.id === notificationId ? {...n, read: true} : n)
      );
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const handleNotificationClick = (notification: any) => {
    // Marcar como lida
    if (!notification.read) {
      markNotificationAsRead(notification.id);
    }

    // Se é notificação de lead, abrir modal de detalhes
    if (notification.type === 'lead' && notification.data?.lead_id) {
      setSelectedLeadId(notification.data.lead_id);
      setShowLeadDetail(true);
    } else {
      // Redirecionar baseado no tipo
      if (notification.type === 'lead') {
        setActiveTab('clients');
      }
    }
    
    setShowNotificacoes(false);
  };

  // Handlers para Fichas de Captação
  const handleEditFicha = (ficha: any) => {
    setSelectedFicha(ficha);
    setShowFichaCaptacaoForm(true);
  };

  const handleViewFicha = (ficha: any) => {
    setSelectedFicha(ficha);
    setShowFichaCaptacaoView(true);
  };

  const handleNewFicha = () => {
    setSelectedFicha(null);
    setShowFichaCaptacaoForm(true);
  };

  const handleSaveFicha = () => {
    setShowFichaCaptacaoForm(false);
    setSelectedFicha(null);
  };

  const handleCancelFicha = () => {
    setShowFichaCaptacaoForm(false);
    setShowFichaCaptacaoView(false);
    setSelectedFicha(null);
  };

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!user || !profile) {
    navigate('/login');
    return null;
  }

  // Calculate statistics - apenas imóveis ativos (mesma lógica do PropertyList)
  const forSaleProperties = properties.filter(p => p.isActive && (
    (p.listPrice && p.listPrice > 0) || p.transactionType === 'Venda' || p.transactionType === 'Venda/Aluguel'
  )).length;
  const forRentProperties = properties.filter(p => p.isActive && (
    (p.rentalPrice && p.rentalPrice > 0) || p.transactionType === 'Aluguel' || p.transactionType === 'Venda/Aluguel'
  )).length;
  
  // Imóveis do usuário logado (captador) - apenas ativos
  const userForSaleProperties = properties.filter(p => 
    p.isActive && p.userId === user.id && (
      (p.listPrice && p.listPrice > 0) || p.transactionType === 'Venda' || p.transactionType === 'Venda/Aluguel'
    )
  ).length;
  const userForRentProperties = properties.filter(p => 
    p.isActive && p.userId === user.id && (
      (p.rentalPrice && p.rentalPrice > 0) || p.transactionType === 'Aluguel' || p.transactionType === 'Venda/Aluguel'
    )
  ).length;

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };


  const handleTypeSelect = (propertyType: string, condominiumId?: string) => {
    console.log('🔍 DashboardPage - handleTypeSelect called:', { propertyType, condominiumId });
    setSelectedPropertyType(propertyType);
    setSelectedCondominiumId(condominiumId);
    setShowTypeSelector(false);
    
    // Se estamos no dashboard, precisamos abrir o formulário diretamente
    if (activeTab === 'dashboard') {
      console.log('🔍 DashboardPage - Opening form with:', { propertyType, condominiumId });
      setShowForm(true);
    } else {
      setActiveTab('properties'); // Navega para a página de imóveis se já estiver em outra aba
    }
  };

  const handleCancelTypeSelector = () => {
    setShowTypeSelector(false);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setSelectedPropertyType('');
    setSelectedCondominiumId(undefined);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(price);
  };

  // Profile functions
  const loadFullProfile = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id,
          first_name,
          last_name,
          email,
          phone,
          role,
          creci,
          manager:manager_id (
            first_name,
            last_name
          )
        `)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setFullProfile(data);
      setProfileData({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        phone: data.phone || '',
        creci: data.creci || ''
      });
      setDisplayProfilePhone(formatPhone(data.phone || ''));
    } catch (error) {
      console.error('Erro ao carregar perfil:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar o perfil",
        variant: "destructive",
      });
    }
  };

  const handleSaveProfile = async () => {
    if (!fullProfile) return;
    
    const cleanPhone = parsePhone(profileData.phone);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: profileData.first_name,
          last_name: profileData.last_name,
          phone: cleanPhone || null,
          creci: profileData.creci || null
        })
        .eq('id', fullProfile.id);

      if (error) throw error;

      // Atualizar o estado local
      setFullProfile({
        ...fullProfile,
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        phone: cleanPhone,
        creci: profileData.creci
      });

      setIsEditingProfile(false);
      toast({
        title: "Perfil atualizado",
        description: "Suas informações foram atualizadas com sucesso",
      });
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o perfil",
        variant: "destructive",
      });
    }
  };

  const handleCancelEdit = () => {
    setProfileData({
      first_name: fullProfile?.first_name || '',
      last_name: fullProfile?.last_name || '',
      phone: fullProfile?.phone || '',
      creci: fullProfile?.creci || ''
    });
    setDisplayProfilePhone(formatPhone(fullProfile?.phone || ''));
    setIsEditingProfile(false);
  };

  const handleProfilePhoneChange = (value: string) => {
    const cleanPhone = parsePhone(value);
    setDisplayProfilePhone(formatPhone(value));
    setProfileData({...profileData, phone: cleanPhone});
  };

  // Função para limpar imagens duplicadas
  const handleCleanDuplicateImages = async () => {
    try {
      toast({
        title: "Limpeza iniciada",
        description: "Processando limpeza das imagens duplicadas...",
      });

      const result = await cleanAllDuplicateImages();
      
      if (result.success) {
        toast({
          title: "Limpeza concluída!",
          description: result.message,
        });
        // Atualizar a lista de propriedades
        await refreshProperties();
      } else {
        toast({
          title: "Erro na limpeza",
          description: result.error,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Erro ao limpar imagens:', error);
      toast({
        title: "Erro",
        description: "Erro interno ao limpar imagens duplicadas",
        variant: "destructive",
      });
    }
  };

  const handleChangePassword = async () => {
    // Validações
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast({
        title: "Erro",
        description: "Todos os campos de senha são obrigatórios",
        variant: "destructive",
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Erro",
        description: "A nova senha e a confirmação devem ser iguais",
        variant: "destructive",
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast({
        title: "Erro",
        description: "A nova senha deve ter pelo menos 6 caracteres",
        variant: "destructive",
      });
      return;
    }

    try {
      // Primeiro, verificar se a senha atual está correta tentando fazer login
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: fullProfile.email,
        password: passwordData.currentPassword,
      });

      if (signInError) {
        toast({
          title: "Erro",
          description: "Senha atual incorreta",
          variant: "destructive",
        });
        return;
      }

      // Se chegou aqui, a senha atual está correta, então pode alterar
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) throw error;

      // Limpar os campos e fechar o formulário
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setIsChangingPassword(false);

      toast({
        title: "Senha alterada",
        description: "Sua senha foi alterada com sucesso",
      });
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      toast({
        title: "Erro",
        description: "Não foi possível alterar a senha",
        variant: "destructive",
      });
    }
  };

  // Handlers específicos para cada campo para evitar re-renders
  const handleCurrentPasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData(prev => ({
      ...prev,
      currentPassword: e.target.value
    }));
  }, []);

  const handleNewPasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData(prev => ({
      ...prev,
      newPassword: e.target.value
    }));
  }, []);

  const handleConfirmPasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData(prev => ({
      ...prev,
      confirmPassword: e.target.value
    }));
  }, []);

  const togglePasswordVisibility = useCallback((field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  }, []);

  const handleCancelPasswordChange = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setIsChangingPassword(false);
  };

  const getRoleLabel = (role: string) => {
    const labels = {
      corretor: 'Corretor',
      gerente: 'Gerente',
      rh: 'RH',
      diretor: 'Diretor'
    };
    return labels[role as keyof typeof labels] || role;
  };

  const getRoleColor = (role: string) => {
    const colors = {
      corretor: 'bg-blue-100 text-blue-800',
      gerente: 'bg-green-100 text-green-800',
      rh: 'bg-purple-100 text-purple-800',
      diretor: 'bg-red-100 text-red-800'
    };
    return colors[role as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const StatCard = ({ title, value, icon: Icon, color, change }: {
    title: string;
    value: string | number;
    icon: any;
    color: string;
    change?: string;
  }) => (
    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 hover:shadow-xl transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
          {change && (
            <p className="text-xs sm:text-sm text-green-600 mt-1">
              <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
              {change}
            </p>
          )}
        </div>
        <div className={`p-2 sm:p-3 rounded-full ${color} shrink-0`}>
          <Icon className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const Sidebar = () => (
    <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-800 transform ${
      sidebarOpen ? 'translate-x-0' : '-translate-x-full'
    } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col`}>
      <div className="flex items-center justify-between h-16 px-6 bg-gray-900 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
            <Home className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-white">Fox Vendas</span>
        </div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden text-gray-400 hover:text-white"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto mt-8">
        <div className="px-4 space-y-2">
          <button
            onClick={() => {
              setActiveTab('dashboard');
              setSidebarOpen(false); // Fecha o menu mobile
            }}
            className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'dashboard'
                ? 'bg-orange-500 text-white'
                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
            }`}
          >
            <BarChart3 className="w-5 h-5 mr-3" />
            Dashboard
          </button>

          <button
            onClick={() => {
              setActiveTab('properties');
              setSidebarOpen(false); // Fecha o menu mobile
            }}
            className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'properties'
                ? 'bg-orange-500 text-white'
                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
            }`}
          >
            <Building className="w-5 h-5 mr-3" />
            Imóveis
          </button>

          <button
            onClick={() => {
              setActiveTab('condominiums');
              setSidebarOpen(false); // Fecha o menu mobile
            }}
            className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'condominiums'
                ? 'bg-orange-500 text-white'
                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
            }`}
          >
            <Building className="w-5 h-5 mr-3" />
            Condomínios
          </button>

          <button
            onClick={() => {
              setActiveTab('clients');
              setSidebarOpen(false); // Fecha o menu mobile
            }}
            className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'clients'
                ? 'bg-orange-500 text-white'
                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
            }`}
          >
            <Users className="w-5 h-5 mr-3" />
            Clientes
          </button>

          <button
            onClick={() => {
              setActiveTab('fila');
              setSidebarOpen(false); // Fecha o menu mobile
            }}
            className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'fila'
                ? 'bg-orange-500 text-white'
                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
            }`}
          >
            <Clock className="w-5 h-5 mr-3" />
            Fila de Leads
          </button>

          {/* Menu Comercial com submenu - Desktop */}
          <div
            onMouseEnter={() => setComercialSubmenuOpen(true)}
            onMouseLeave={() => setComercialSubmenuOpen(false)}
            className="hidden md:block"
          >
            <button
              onClick={() => {
                setComercialSubmenuOpen(!comercialSubmenuOpen);
              }}
              className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'vendas' || activeTab === 'locacoes' || activeTab === 'templates-contrato' || activeTab === 'cabecalho-contratos'
                  ? 'bg-orange-500 text-white'
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <Briefcase className="w-5 h-5 mr-3" />
                Comercial
              </div>
              {comercialSubmenuOpen ? 
                <ChevronDown className="w-4 h-4" /> : 
                <ChevronRight className="w-4 h-4" />
              }
            </button>
            
            {comercialSubmenuOpen && (
              <div className="ml-4 mt-2 space-y-1">
                <button
                  onClick={() => {
                    setActiveTab('vendas');
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-start text-left px-8 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'vendas'
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Vendas
                </button>
                
                <button
                  onClick={() => {
                    setActiveTab('locacoes');
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-start text-left px-8 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'locacoes'
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Locações
                </button>
                
                <button
                  onClick={() => {
                    setActiveTab('templates-contrato');
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-start text-left px-8 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'templates-contrato'
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Templates de Contrato
                </button>
                
                <button
                  onClick={() => {
                    setActiveTab('cabecalho-contratos');
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-start text-left px-8 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'cabecalho-contratos'
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Cabeçalho de Contratos
                </button>
              </div>
            )}
          </div>

          {/* Menu Comercial com submenu - Mobile */}
          <div className="md:hidden">
            <button
              onClick={() => {
                setComercialSubmenuOpen(!comercialSubmenuOpen);
              }}
              className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'vendas' || activeTab === 'locacoes' || activeTab === 'templates-contrato' || activeTab === 'cabecalho-contratos'
                  ? 'bg-orange-500 text-white'
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <Briefcase className="w-5 h-5 mr-3" />
                Comercial
              </div>
              {comercialSubmenuOpen ? 
                <ChevronDown className="w-4 h-4" /> : 
                <ChevronRight className="w-4 h-4" />
              }
            </button>
            
            {comercialSubmenuOpen && (
              <div className="ml-4 mt-2 space-y-1">
                <button
                  onClick={() => {
                    setActiveTab('vendas');
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-start text-left px-8 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'vendas'
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Vendas
                </button>
                
                <button
                  onClick={() => {
                    setActiveTab('locacoes');
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-start text-left px-8 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'locacoes'
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Locações
                </button>
                
                <button
                  onClick={() => {
                    setActiveTab('templates-contrato');
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-start text-left px-8 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'templates-contrato'
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Templates de Contrato
                </button>
                
                <button
                  onClick={() => {
                    setActiveTab('cabecalho-contratos');
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-start text-left px-8 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'cabecalho-contratos'
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Cabeçalho de Contratos
                </button>
              </div>
            )}
          </div>

          <button
            onClick={() => setActiveTab('fichas-captacao')}
            className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'fichas-captacao'
                ? 'bg-orange-600 text-white'
                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
            }`}
          >
            <FileText className="w-5 h-5 mr-3" />
            Ficha de Captação
          </button>

          {canAccessSettings() && (
            <div
              onMouseEnter={() => setConfigSubmenuOpen(true)}
              onMouseLeave={() => setConfigSubmenuOpen(false)}
              className="hidden md:block"
            >
              <button
                onClick={() => {
                  setConfigSubmenuOpen(!configSubmenuOpen);
                }}
                className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'settings' || activeTab === 'usuarios'
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <div className="flex items-center">
                  <Settings className="w-5 h-5 mr-3" />
                  Configurações
                </div>
                {configSubmenuOpen ? 
                  <ChevronDown className="w-4 h-4" /> : 
                  <ChevronRight className="w-4 h-4" />
                }
              </button>
              
              {/* Sub-menu for Configurações */}
              {configSubmenuOpen && (
                <div className="ml-4 space-y-1">
                  <button
                    onClick={() => {
                      setActiveTab('settings');
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center justify-start px-8 py-2 text-sm font-medium rounded-lg transition-colors ${
                      activeTab === 'settings'
                        ? 'bg-orange-400 text-white'
                        : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Geral
                  </button>
                  
                  {/* Sub-menu for Usuários */}
                  {canAccessUsers() && (
                    <button
                      onClick={() => {
                        setActiveTab('usuarios');
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center justify-start px-8 py-2 text-sm font-medium rounded-lg transition-colors ${
                        activeTab === 'usuarios'
                          ? 'bg-orange-400 text-white'
                          : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                      }`}
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Usuários
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Mobile version - mantém comportamento de click */}
          {canAccessSettings() && (
            <div className="md:hidden">
              <button
                onClick={() => {
                  setConfigSubmenuOpen(!configSubmenuOpen);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'settings' || activeTab === 'usuarios'
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <div className="flex items-center">
                  <Settings className="w-5 h-5 mr-3" />
                  Configurações
                </div>
                {configSubmenuOpen ? 
                  <ChevronDown className="w-4 h-4" /> : 
                  <ChevronRight className="w-4 h-4" />
                }
              </button>
              
              {configSubmenuOpen && (
                <div className="ml-4 space-y-1">
                  <button
                    onClick={() => {
                      setActiveTab('settings');
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center justify-start px-8 py-2 text-sm font-medium rounded-lg transition-colors ${
                      activeTab === 'settings'
                        ? 'bg-orange-400 text-white'
                        : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Geral
                  </button>
                  
                  {canAccessUsers() && (
                    <button
                      onClick={() => {
                        setActiveTab('usuarios');
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center px-8 py-2 text-sm font-medium rounded-lg transition-colors ${
                        activeTab === 'usuarios'
                          ? 'bg-orange-400 text-white'
                          : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                      }`}
                    >
                      <UserPlus className="w-4 h-4 mr-3" />
                      Usuários
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

      </nav>
    </div>
  );

  const DashboardContent = () => (
    <div className="space-y-6">
      {/* Card de Leads Não Atendidos - Posição Prioritária */}
      <LeadsNaoAtendidos 
        onViewLead={(leadId) => {
          setSelectedLeadId(leadId);
          setShowLeadDetail(true);
        }}
        onGoToLeads={() => setActiveTab('clients')}
      />
      
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Olá, {profile.first_name}! 👋
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Aqui está um resumo do seu portfólio de imóveis
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Imóveis para Venda"
          value={forSaleProperties}
          icon={Home}
          color="bg-green-500"
          change="+12% este mês"
        />
        <StatCard
          title="Imóveis para Locação"
          value={forRentProperties}
          icon={Home}
          color="bg-blue-500"
          change="+8% este mês"
        />
        <StatCard
          title="Seus Imóveis para Venda"
          value={userForSaleProperties}
          icon={Building}
          color="bg-green-500"
          change="+15% este mês"
        />
        <StatCard
          title="Seus Imóveis para Locação"
          value={userForRentProperties}
          icon={Building}
          color="bg-blue-500"
          change="+10% este mês"
        />
      </div>

      {/* Recent Properties */}
      <div className="bg-white rounded-xl shadow-lg">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">
              Imóveis Recentes
            </h2>
            <button
              onClick={() => setActiveTab('properties')}
              className="text-orange-500 hover:text-orange-600 text-xs sm:text-sm font-medium text-left sm:text-right"
            >
              Ver todos
            </button>
          </div>
        </div>
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {properties.filter(p => p.isActive).slice(0, 6).map((property) => (
              <div 
                key={property.listingId} 
                className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow relative bg-white cursor-pointer"
                onClick={() => window.open(`/imovel/${property.listingId}`, '_blank')}
              >
                {/* Imagem */}
                <div className="relative">
                  <img 
                    src={property.media.images[0]?.url} 
                    alt={property.title}
                    className="w-full h-32 sm:h-40 object-cover"
                   />
                   {/* Badge de tipo de transação */}
                   <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-medium ${
                     property.transactionType === 'Venda' ? 'bg-green-500 text-white' : 
                     property.transactionType === 'Aluguel' ? 'bg-blue-500 text-white' :
                     property.transactionType === 'Venda/Aluguel' ? 'bg-purple-500 text-white' :
                     'bg-gray-500 text-white'
                   }`}>
                     {property.transactionType}
                   </div>
                   {/* Badge de categoria (se for premium) */}
                   {property.category === 'Luxo' && (
                    <div className="absolute top-2 right-2 bg-purple-500 text-white px-2 py-1 rounded text-xs font-medium">
                      Luxo
                    </div>
                  )}
                </div>

                {/* Conteúdo */}
                <div className="p-3 sm:p-4 relative">
                  <h3 className="font-medium text-gray-900 mb-1 line-clamp-2 text-xs sm:text-sm">
                    {property.title}
                  </h3>
                  
                  <p className="text-xs text-gray-500 mb-2 sm:mb-3 truncate">
                    {property.location.neighborhood}, {property.location.city}
                  </p>

                  {/* Características */}
                  <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3 text-xs text-gray-600 overflow-hidden">
                    {property.bedrooms > 0 && (
                      <div className="flex items-center space-x-1 shrink-0">
                        <Bed className="w-3 h-3" />
                        <span>{property.bedrooms}</span>
                      </div>
                    )}
                    {property.bathrooms > 0 && (
                      <div className="flex items-center space-x-1 shrink-0">
                        <Bath className="w-3 h-3" />
                        <span>{property.bathrooms}</span>
                      </div>
                    )}
                    {property.garage?.quantity > 0 && (
                      <div className="flex items-center space-x-1 shrink-0">
                        <Car className="w-3 h-3" />
                        <span>{property.garage.quantity}</span>
                      </div>
                    )}
                    {property.livingArea && (
                      <div className="flex items-center space-x-1 shrink-0">
                        <Ruler className="w-3 h-3" />
                        <span>{property.livingArea}m²</span>
                      </div>
                    )}
                  </div>

                  {/* Preço */}
                  <div className="mb-2">
                    {property.transactionType === 'Venda/Aluguel' ? (
                      <div className="space-y-1">
                        <p className="text-xs sm:text-sm font-bold text-green-600">
                          Venda: {formatPrice(property.listPrice)}
                        </p>
                        <p className="text-xs sm:text-sm font-bold text-blue-600">
                          Locação: {formatPrice(property.rentalPrice || 0)}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm sm:text-lg font-bold text-orange-600">
                        {property.transactionType === 'Aluguel' ? formatPrice(property.rentalPrice || 0) : formatPrice(property.listPrice)}
                      </p>
                    )}
                    
                    {/* Condomínio, IPTU */}
                    <div className="flex flex-col space-y-0.5 text-xs text-gray-600 mt-1">
                      {property.propertyAdministrationFee && property.propertyAdministrationFee > 0 && (
                        <span>Cond: {formatPrice(property.propertyAdministrationFee)}</span>
                      )}
                      {property.yearlyTax && property.yearlyTax > 0 && (
                        <span>IPTU: {formatPrice(property.yearlyTax)}/mês</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const ProfileContent = () => {
    if (!fullProfile) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      );
    }

    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 sm:px-6 py-6 sm:py-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-0 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-lg sm:text-2xl font-bold mx-auto sm:mx-0">
                  {getInitials(fullProfile.first_name, fullProfile.last_name)}
                </div>
                <div className="text-center sm:text-left">
                  <h1 className="text-xl sm:text-3xl font-bold text-gray-900">
                    {fullProfile.first_name} {fullProfile.last_name}
                  </h1>
                  <div className="flex items-center justify-center sm:justify-start space-x-3 mt-2">
                    <span className={`inline-flex px-3 py-1 text-xs sm:text-sm font-semibold rounded-full ${getRoleColor(fullProfile.role)}`}>
                      {getRoleLabel(fullProfile.role)}
                    </span>
                  </div>
                </div>
              </div>
              
              {!isEditingProfile ? (
                <Button 
                  onClick={() => setIsEditingProfile(true)}
                  className="flex items-center justify-center gap-2 w-full sm:w-auto"
                  size="sm"
                >
                  <Edit className="h-4 w-4" />
                  <span className="sm:inline">Editar Perfil</span>
                </Button>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <Button 
                    onClick={handleSaveProfile}
                    className="flex items-center justify-center gap-2"
                    size="sm"
                  >
                    <Save className="h-4 w-4" />
                    Salvar
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleCancelEdit}
                    className="flex items-center justify-center gap-2"
                    size="sm"
                  >
                    <X className="h-4 w-4" />
                    Cancelar
                  </Button>
                </div>
              )}
            </div>

            {/* Informações do perfil */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome
                  </label>
                  {isEditingProfile ? (
                    <input
                      type="text"
                      value={profileData.first_name}
                      onChange={(e) => setProfileData({...profileData, first_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  ) : (
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-900">{fullProfile.first_name}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sobrenome
                  </label>
                  {isEditingProfile ? (
                    <input
                      type="text"
                      value={profileData.last_name}
                      onChange={(e) => setProfileData({...profileData, last_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  ) : (
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-900">{fullProfile.last_name}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900">{fullProfile.email}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">O email não pode ser alterado</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                   {isEditingProfile ? (
                     <input
                       type="tel"
                       value={displayProfilePhone}
                       onChange={(e) => handleProfilePhoneChange(e.target.value)}
                       placeholder="(11) 99999-9999"
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                     />
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-900">{fullProfile.phone || 'Não informado'}</span>
                    </div>
                  )}
                </div>

                {['corretor', 'gerente', 'diretor'].includes(fullProfile.role) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CRECI
                    </label>
                    {isEditingProfile ? (
                      <input
                        type="text"
                        value={profileData.creci}
                        onChange={(e) => setProfileData({...profileData, creci: e.target.value})}
                        placeholder="CRECI/SP 123456"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Award className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-900">{fullProfile.creci || 'Não informado'}</span>
                      </div>
                    )}
                  </div>
                )}

                {fullProfile.manager && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gerente
                    </label>
                    <div className="flex items-center space-x-2">
                      <Building className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-900">
                        {fullProfile.manager.first_name} {fullProfile.manager.last_name}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Seção de Mudança de Senha */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Segurança</h2>
                <p className="text-gray-600">Altere sua senha de acesso</p>
              </div>
              
              {!isChangingPassword ? (
                <Button 
                  onClick={() => setIsChangingPassword(true)}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Lock className="h-4 w-4" />
                  Alterar Senha
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    onClick={handleChangePassword}
                    className="flex items-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    Salvar Nova Senha
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleCancelPasswordChange}
                    className="flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    Cancelar
                  </Button>
                </div>
              )}
            </div>

            {isChangingPassword && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Senha Atual
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.current ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={handleCurrentPasswordChange}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Digite sua senha atual"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('current')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nova Senha
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={handleNewPasswordChange}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Digite a nova senha (mín. 6 caracteres)"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('new')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirmar Nova Senha
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Confirme a nova senha"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('confirm')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Estados para XML feed (mover para fora do componente)
  const [xmlContent, setXmlContent] = useState<string>('');
  const [xmlLoading, setXmlLoading] = useState(false);

  const SettingsContent = () => {
    // Debug do estado atual sempre que renderizar
    console.log('🎨 SettingsContent renderizando. Estado atual:');
    console.log('   - xmlContent length:', xmlContent?.length || 0);
    console.log('   - xmlContent existe?', !!xmlContent);
    console.log('   - loading:', xmlLoading);

    const fetchXMLFeed = async () => {
      setXmlLoading(true);
      try {
        console.log('🔄 Iniciando busca do XML feed via Supabase...');
        
        // Usar o supabase client para chamar a edge function com headers corretos
        const { data, error } = await supabase.functions.invoke('xml-feed', {
          method: 'GET',
          headers: {
            'Accept': 'application/xml'
          }
        });
        
        console.log('📡 Resposta da edge function:', { data, error });
        
        if (error) {
          throw new Error(`Erro na edge function: ${error.message}`);
        }
        
        // O data já deve ser o XML como string
        const xmlText = typeof data === 'string' ? data : JSON.stringify(data);
        console.log('📄 XML recebido:');
        console.log('   - Tamanho:', xmlText.length, 'caracteres');
        console.log('   - Primeiros 200 chars:', xmlText.substring(0, 200));
        console.log('   - É string válida?', typeof xmlText === 'string');
        console.log('   - Tem conteúdo?', xmlText.length > 0);
        
        // Verificar se realmente contém XML
        if (!xmlText || xmlText.length === 0) {
          throw new Error('XML retornado está vazio');
        }
        
        if (!xmlText.includes('<?xml') && !xmlText.includes('<ListingDataFeed')) {
          console.error('❌ Conteúdo recebido não parece ser XML válido:', xmlText.substring(0, 500));
          throw new Error('Conteúdo recebido não é um XML válido');
        }
        
        setXmlContent(xmlText);
        console.log('✅ setXmlContent chamado com:', xmlText.length, 'caracteres');
        
        // Verificar se o estado foi realmente atualizado (aguardar próximo render)
        setTimeout(() => {
          console.log('🔍 Verificando estado após setXmlContent:');
          console.log('   - xmlContent atual length:', xmlContent?.length || 0);
          console.log('   - xmlContent é truthy?', !!xmlContent);
        }, 100);
        
        toast({
          title: "Sucesso",
          description: `XML carregado com sucesso! (${(xmlText.length / 1024).toFixed(1)} KB)`,
        });
        
      } catch (error) {
        console.error('❌ Erro ao buscar XML:', error);
        setXmlContent(''); // Limpar conteúdo em caso de erro
        toast({
          title: "Erro",
          description: `Erro ao buscar XML feed: ${error instanceof Error ? error.message : 'Erro desconhecido'}`,
          variant: "destructive",
        });
      } finally {
        setXmlLoading(false);
        console.log('🏁 Processo finalizado. Loading:', false);
      }
    };

    const downloadXML = () => {
      console.log('🔽 Tentativa de download:');
      console.log('   - xmlContent existe?', !!xmlContent);
      console.log('   - xmlContent length:', xmlContent?.length || 0);
      console.log('   - xmlContent type:', typeof xmlContent);
      
      if (!xmlContent) {
        console.log('❌ Download bloqueado: xmlContent está vazio');
        toast({
          title: "Atenção",
          description: "Primeiro carregue o XML clicando em 'Carregar XML'",
          variant: "destructive",
        });
        return;
      }

      try {
        // Criar blob com tipo MIME correto
        const blob = new Blob([xmlContent], { 
          type: 'application/xml;charset=utf-8' 
        });
        
        // Criar URL para o blob
        const url = URL.createObjectURL(blob);
        
        // Criar elemento de link e simular clique
        const link = document.createElement('a');
        link.href = url;
        link.download = `fox-vendas-feed-${new Date().toISOString().split('T')[0]}.xml`;
        link.style.display = 'none';
        
        // Adicionar ao DOM, clicar e remover
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Limpar URL do blob
        URL.revokeObjectURL(url);
        
        console.log('Download iniciado com sucesso');
        toast({
          title: "Sucesso",
          description: "Download do XML iniciado!",
        });
        
      } catch (error) {
        console.error('Erro no download:', error);
        toast({
          title: "Erro",
          description: `Erro ao fazer download: ${error instanceof Error ? error.message : 'Erro desconhecido'}`,
          variant: "destructive",
        });
      }
    };

    const copyXMLUrl = () => {
      const xmlUrl = 'https://ngagtienwooguwquvtbn.supabase.co/functions/v1/xml-feed';
      
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(xmlUrl).then(() => {
          toast({
            title: "Sucesso",
            description: "URL copiada para a área de transferência!",
          });
        }).catch(error => {
          console.error('Erro ao copiar:', error);
          // Fallback para navegadores antigos
          fallbackCopyToClipboard(xmlUrl);
        });
      } else {
        // Fallback para navegadores antigos
        fallbackCopyToClipboard(xmlUrl);
      }
    };

    const fallbackCopyToClipboard = (text: string) => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        toast({
          title: "Sucesso",
          description: "URL copiada para a área de transferência!",
        });
      } catch (error) {
        toast({
          title: "Erro",
          description: "Não foi possível copiar a URL",
          variant: "destructive",
        });
      }
      
      document.body.removeChild(textArea);
    };

    return (
      <div className="space-y-8">
        {/* Seção de Integração com OLX */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-8">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Integração com Grupo OLX</h2>
              <p className="text-gray-600">
                Configure e monitore a integração com VivaReal, ZAP Imóveis e OLX
              </p>
            </div>
            
            <div className="space-y-6">
              {/* Botões de Ação */}
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={fetchXMLFeed}
                  disabled={xmlLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {xmlLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Carregando...
                    </>
                  ) : (
                    'Carregar XML'
                  )}
                </Button>
                
                <Button
                  onClick={downloadXML}
                  disabled={!xmlContent || xmlLoading}
                  variant="outline"
                  className={`border-green-600 text-green-600 hover:bg-green-50 disabled:opacity-50 ${
                    !xmlContent ? 'cursor-not-allowed' : ''
                  }`}
                  title={!xmlContent ? 'Primeiro carregue o XML' : 'Baixar arquivo XML'}
                >
                  📥 Baixar XML 
                  {xmlContent && ` (${(xmlContent.length / 1024).toFixed(1)} KB)`}
                  {/* Debug visual */}
                  <span className="ml-2 text-xs opacity-60">
                    {xmlContent ? '✅' : '❌'}
                  </span>
                </Button>
                
                <Button
                  onClick={copyXMLUrl}
                  variant="outline"
                  className="border-purple-600 text-purple-600 hover:bg-purple-50"
                >
                  📋 Copiar URL do Feed
                </Button>
                
                {/* Botão para limpar imagens duplicadas - temporário */}
                <Button
                  onClick={handleCleanDuplicateImages}
                  variant="outline"
                  className="border-orange-600 text-orange-600 hover:bg-orange-50"
                >
                  🧹 Limpar Imagens Duplicadas
                </Button>
              </div>

              {/* Instruções */}
              {!xmlContent && !xmlLoading && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</div>
                    <span className="text-blue-800 font-medium">
                      Primeiro clique em "Carregar XML" para buscar os dados
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 mt-2">
                    <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center text-white text-sm font-bold">2</div>
                    <span className="text-gray-600">
                      Depois o botão de download será habilitado
                    </span>
                  </div>
                </div>
              )}

              {/* Informações da URL */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">URL do Feed XML para Grupo OLX:</h3>
                <code className="bg-white p-3 rounded border block text-sm font-mono break-all">
                  https://ngagtienwooguwquvtbn.supabase.co/functions/v1/xml-feed
                </code>
                <p className="text-sm text-gray-600 mt-2">
                  Esta URL deve ser fornecida ao Grupo OLX para integração automática dos imóveis
                </p>
              </div>

              {/* Status do XML */}
              {xmlContent && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-green-800 font-medium">
                      XML carregado ({(xmlContent.length / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <p className="text-green-700 text-sm mt-1">
                    Pronto para download ou visualização
                  </p>
                </div>
              )}

              {/* Visualização do XML */}
              {xmlContent && (
                <div className="border rounded-lg">
                  <div className="bg-gray-50 p-3 border-b flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Conteúdo XML Gerado:</h3>
                    <span className="text-sm text-gray-500">
                      {xmlContent.split('<Listing>').length - 1} imóveis encontrados
                    </span>
                  </div>
                  <pre className="p-4 overflow-auto max-h-96 text-sm bg-white font-mono">
                    {xmlContent}
                  </pre>
                </div>
              )}

              {!xmlContent && !xmlLoading && (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                  <Settings className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>Clique em "Carregar XML" para visualizar o feed de imóveis</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Auto-Update OLX */}
        <AutoUpdateManager />

        {/* Test Video Update - Temporário */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4">
            <TestVideoUpdate />
          </div>
        </div>

        {/* Outras configurações futuras */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-8">
            <div className="text-center py-8">
              <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Outras Configurações
              </h3>
              <p className="text-gray-600">
                Funcionalidades adicionais em desenvolvimento
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardContent />;
      case 'properties':
        return <PropertyList />;
      case 'condominiums':
        return <CondominiumList />;
      case 'clients':
        return <ClientList />;
      case 'fila':
        return <FilaDistribuicao />;
      case 'vendas':
        return <ComercialDashboard initialTab="vendas" />;
      case 'locacoes':
        return <ComercialDashboard initialTab="locacoes" />;
      case 'templates-contrato':
        return <TemplatesContrato />;
      case 'cabecalho-contratos':
        return <ConfiguracaoCabecalhoContrato />;
      case 'rh':
        return <RHDashboard />;
      case 'usuarios':
        return <UserManagement />;
      case 'fichas-captacao':
        return showFichaCaptacaoForm ? (
          <FichaCaptacaoForm 
            ficha={selectedFicha}
            onSave={handleSaveFicha}
            onCancel={handleCancelFicha}
          />
        ) : showFichaCaptacaoView ? (
          <FichaCaptacaoView 
            ficha={selectedFicha}
            onEdit={() => handleEditFicha(selectedFicha)}
            onBack={handleCancelFicha}
          />
        ) : (
          <FichaCaptacaoList 
            onEdit={handleEditFicha}
            onView={handleViewFicha}
            onNew={handleNewFicha}
          />
        );
      case 'perfil':
        return <ProfileContent />;
      case 'settings':
        return <SettingsContent />;
      default:
        return <DashboardContent />;
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-600 hover:text-gray-900"
              >
                <Menu className="w-6 h-6" />
              </button>
              
              <Link 
                to="/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-orange-500 text-sm font-medium"
              >
                Ver Site
              </Link>
            </div>

            <div className="flex items-center space-x-4">
              {/* Central de Notificações */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowNotificacoes(!showNotificacoes);
                    if (!showNotificacoes) {
                      // Marcar notificações não lidas como lidas quando abrir
                      const unreadNotifications = notificacoes.filter(n => !n.read);
                      unreadNotifications.forEach(n => markNotificationAsRead(n.id));
                      setHasNotifications(false);
                    }
                  }}
                  className="relative p-2 text-gray-600 hover:text-orange-500 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Notificações"
                >
                  <Bell 
                    className={`w-5 h-5 ${hasNotifications ? 'text-primary' : 'text-muted-foreground'}`}
                  />
                  {hasNotifications && notificacoes.filter(n => !n.read).length > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 bg-destructive text-destructive-foreground rounded-full border-2 border-background flex items-center justify-center px-1">
                      <span className="text-[10px] font-bold">
                        {notificacoes.filter(n => !n.read).length > 9 ? '9+' : notificacoes.filter(n => !n.read).length}
                      </span>
                    </span>
                  )}
                </button>

                {showNotificacoes && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-3 border-b border-gray-200">
                      <h3 className="font-medium text-gray-900">Notificações</h3>
                    </div>
                    {notificacoes.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        Nenhuma notificação
                      </div>
                    ) : (
                      <div className="max-h-64 overflow-y-auto">
                        {notificacoes.map((notif) => (
                          <div 
                            key={notif.id} 
                            className={`p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                              !notif.read ? 'bg-orange-50' : ''
                            }`}
                            onClick={() => handleNotificationClick(notif)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className={`text-sm ${!notif.read ? 'font-semibold text-gray-900' : 'text-gray-900'}`}>
                                  {notif.title}
                                </p>
                                <p className="text-sm text-gray-600 mt-1">{notif.message}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(notif.created_at).toLocaleString('pt-BR')}
                                </p>
                              </div>
                              {!notif.read && (
                                <span className="w-2 h-2 bg-orange-500 rounded-full mt-1"></span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Avatar do usuário com dropdown no mobile */}
              <div className="relative avatar-menu">
                <button
                  onClick={() => {
                    // Desktop: vai para o perfil
                    // Mobile: abre dropdown
                    if (window.innerWidth <= 768) {
                      setShowAvatarMenu(!showAvatarMenu);
                    } else {
                      setActiveTab('perfil');
                      setSidebarOpen(false);
                      loadFullProfile();
                    }
                  }}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors ${
                    activeTab === 'perfil' ? 'bg-orange-50 ring-2 ring-orange-500' : ''
                  }`}
                >
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {profile.first_name.charAt(0)}
                    </span>
                  </div>
                  {/* Texto do nome - oculto no mobile */}
                  <div className="text-left hidden md:block">
                    <p className="text-gray-900 text-sm font-medium">
                      {profile.first_name} {profile.last_name}
                    </p>
                    <p className="text-gray-500 text-xs">{profile.email}</p>
                  </div>
                </button>

                {/* Dropdown menu - apenas mobile */}
                {showAvatarMenu && (
                  <div className="absolute right-0 top-12 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 md:hidden">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-gray-900 text-sm font-medium">
                        {profile.first_name} {profile.last_name}
                      </p>
                      <p className="text-gray-500 text-xs">{profile.email}</p>
                    </div>
                    
                    <button
                      onClick={() => {
                        setActiveTab('perfil');
                        setSidebarOpen(false);
                        setShowAvatarMenu(false);
                        loadFullProfile();
                      }}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <User className="w-4 h-4 mr-3" />
                      Meu Perfil
                    </button>
                    
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Sair
                    </button>
                  </div>
                )}
              </div>

              {/* Botão de sair separado - apenas desktop */}
              <button
                onClick={handleLogout}
                className="hidden md:flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors"
                title="Sair"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          {renderContent()}
        </main>

        {/* Lead Detail Modal */}
        {showLeadDetail && selectedLeadId && (
          <LeadDetailModal
            isOpen={showLeadDetail}
            onClose={() => {
              setShowLeadDetail(false);
              setSelectedLeadId(null);
            }}
            leadId={selectedLeadId}
          />
        )}

        {/* Type Selector Modal */}
        {showTypeSelector && (
          <PropertyTypeSelector
            onSelect={handleTypeSelect}
            onCancel={handleCancelTypeSelector}
          />
        )}

        {/* Property Form Modal - only show in dashboard */}
        {showForm && activeTab === 'dashboard' && (
          <PropertyForm
            initialPropertyType={selectedPropertyType}
            initialCondominiumId={selectedCondominiumId}
            onSave={async (property) => {
              console.log('🎯 onSave INICIADO no DashboardPage');
              console.log('📦 property recebida:', property);
              
              // Validações iniciais
              if (!property.userId) {
                console.error('❌ property.userId está vazio:', property.userId);
                alert('Erro: ID do usuário não encontrado.');
                return;
              }
              
              if (!property.title) {
                console.error('❌ property.title está vazio:', property.title);
                alert('Erro: Título do imóvel é obrigatório.');
                return;
              }
              
              try {
                console.log('🖼️ Processando imagens:', property.media?.images);
                
                // Upload das imagens para o Supabase Storage
                const uploadedImages = [];
                
                if (property.media?.images?.length > 0) {
                  for (let i = 0; i < property.media.images.length; i++) {
                    const image = property.media.images[i];
                    
                    // Se a imagem tem um arquivo (File object), fazer upload
                    if (image.file) {
                      console.log(`📤 Fazendo upload da imagem ${i + 1}:`, image.name);
                      
                      const fileExt = image.file.name.split('.').pop();
                      const fileName = `${property.listingId || Date.now()}/${Date.now()}_${i}.${fileExt}`;
                      
                      const { error: uploadError } = await supabase.storage
                        .from('property-images')
                        .upload(fileName, image.file);
                      
                      if (uploadError) {
                        console.error('❌ Erro no upload da imagem:', uploadError);
                        throw uploadError;
                      }
                      
                      // Obter URL pública da imagem
                      const { data: urlData } = supabase.storage
                        .from('property-images')
                        .getPublicUrl(fileName);
                      
                      uploadedImages.push({
                        id: image.id,
                        url: urlData.publicUrl,
                        name: image.name,
                        primary: image.primary
                      });
                      
                      console.log(`✅ Upload concluído para imagem ${i + 1}:`, urlData.publicUrl);
                    } else if (image.url) {
                      // Se já tem URL (edição), manter a imagem existente
                      uploadedImages.push({
                        id: image.id,
                        url: image.url,
                        name: image.name,
                        primary: image.primary
                      });
                    }
                  }
                }
                
                const imagesJson = uploadedImages;

                const propertyData = {
                  title: property.title,
                  description: property.description || '',
                  property_type: propertyTypeToEng(property.propertyType),
                  transaction_type: transactionTypeToEng(property.transactionType),
                  list_price: property.listPrice,
                  rental_price: property.rentalPrice || null,
                  condominium_fee: property.propertyAdministrationFee || null,
                  yearly_iptu: property.yearlyTax || null,
                  bedrooms: property.bedrooms || 0,
                  bathrooms: property.bathrooms || 0,
                  suites: property.suites || 0,
                  area_built: property.livingArea || null,
                  area_total: property.livingArea || null,
                  garage_quantity: property.garage?.quantity || 0,
                  neighborhood: property.location?.neighborhood || '',
                  city: property.location?.city || '',
                  state: property.location?.state || '',
                  address: property.location?.address || '',
                  zip_code: property.location?.postalCode || '',
                  latitude: property.location?.latitude || null,
                  longitude: property.location?.longitude || null,
                  owner_name: property.owner?.name || '',
                  owner_phone: property.owner?.phone || '',
                  owner_email: property.owner?.email || '',
                  images: imagesJson,
                  amenities: property.features || [],
                  user_id: user?.id || '', // Quem está criando/editando
                  responsible_broker_id: property.userId, // Corretor selecionado como captador
                  listing_id: property.listingId,
                  video_url: property.media?.youtubeVideoUrl || null,
                  olx: property.olx
                };

                console.log('💰 DEBUG - Valores financeiros enviados:');
                console.log('  property.propertyAdministrationFee:', property.propertyAdministrationFee);
                console.log('  property.yearlyTax:', property.yearlyTax);
                console.log('  condominium_fee no propertyData:', propertyData.condominium_fee);
                console.log('  yearly_iptu no propertyData:', propertyData.yearly_iptu);

                console.log('🏗️ propertyData para Supabase:', propertyData);
                console.log('🖼️ DEBUG - imagesJson que será salvo:', imagesJson);
                console.log('📡 Enviando para Supabase...');
                
                const { data, error } = await supabase
                  .from('properties')
                  .insert(propertyData)
                  .select()
                  .single();

                if (error) {
                  console.error('❌ Erro DETALHADO do Supabase:', {
                    message: error.message,
                    details: error.details,
                    hint: error.hint,
                    code: error.code
                  });
                  alert(`Erro ao salvar imóvel: ${error.message}\nDetalhes: ${error.details || 'Nenhum detalhe adicional'}`);
                  return;
                }

                console.log('✅ Imóvel salvo com sucesso no Supabase:', data);
                
                // Atualizar o contexto para refletir a mudança na lista
                await refreshProperties();
                
                toast({
                  title: "Sucesso",
                  description: "Imóvel cadastrado com sucesso!",
                });
                
                setShowForm(false);
                setSelectedPropertyType('');
                setSelectedCondominiumId(undefined);
                
                console.log('🎊 Processo de salvamento CONCLUÍDO');
              } catch (error: any) {
                console.error('💥 Erro CRÍTICO no processo de salvamento:', {
                  message: error?.message,
                  stack: error?.stack,
                  error: error
                });
                alert(`Erro inesperado: ${error?.message || error}`);
              }
            }}
            onCancel={handleCancelForm}
          />
        )}

        {/* Modal de Detalhes do Lead */}
        {selectedLeadId && (
          <LeadDetailModal
            leadId={selectedLeadId}
            isOpen={showLeadDetail}
            onClose={() => {
              setShowLeadDetail(false);
              setSelectedLeadId(null);
            }}
          />
        )}

        {/* Modal de Detalhes do Imóvel */}
        {viewingProperty && (
          <PropertyDetailModal
            property={viewingProperty}
            onClose={() => setViewingProperty(undefined)}
          />
        )}


        {/* Modal de Troca Forçada de Senha */}
        <ForcePasswordChangeModal 
          isOpen={!!(profile?.force_password_change)} 
        />
      </div>
    </div>
  );
  } catch (error) {
    console.error('🚨 DashboardPage: Erro no componente:', error);
    throw error; // Re-throw para que o ErrorBoundary capture
  }
};

export default DashboardPage;